// CsvImportExport — deterministic CSV export + import (no AI).
// Generic: the parent supplies the export columns/rows and an onImport handler.
// Used by the Price Tool (menu items) and the Daily Log. Replaces the old
// AI-powered CsvImporter for predictable, free, round-trippable import/export.
import { useRef, useState } from "react";
import { C } from "../lib/theme";
import { Btn } from "./ui";
import { toCSV, downloadCSV, parseCSV } from "../lib/csv";

export function CsvImportExport({
  title,
  subtitle,
  templateHeaders,       // string[] — header row for export + blank template
  buildExportRows,       // () => any[][] — current data as row-arrays
  exportFilename,        // string
  onImport,              // async (parsedRows) => { added, updated, skipped, errors:[] }
  noun = "rows",         // label for the summary ("items" / "entries")
}) {
  const [stage, setStage] = useState("idle"); // idle | preview | importing | done
  const [parsed, setParsed] = useState(null);
  const [summary, setSummary] = useState(null);
  const [err, setErr] = useState("");
  const fileRef = useRef(null);

  function doExport() {
    const rows = (buildExportRows && buildExportRows()) || [];
    downloadCSV(exportFilename, toCSV(templateHeaders, rows));
  }

  function downloadTemplate() {
    // Headers only + one example-blank row so the columns are obvious.
    downloadCSV(
      exportFilename.replace(/\.csv$/i, "") + "-template.csv",
      toCSV(templateHeaders, [])
    );
  }

  async function handleFile(file) {
    setErr("");
    if (!file || !/\.(csv|txt)$/i.test(file.name)) { setErr("Please choose a .csv file."); return; }
    const text = await file.text();
    const res = parseCSV(text);
    if (!res.rows.length) { setErr("That file has no data rows."); return; }
    setParsed(res);
    setStage("preview");
  }

  async function confirmImport() {
    setStage("importing"); setErr("");
    try {
      const result = await onImport(parsed.rows);
      setSummary(result || { added: 0, updated: 0, skipped: 0, errors: [] });
      setStage("done");
    } catch (e) {
      setErr("Import failed — " + (e?.message || "unknown error"));
      setStage("preview");
    }
  }

  function reset() {
    setStage("idle"); setParsed(null); setSummary(null); setErr("");
    if (fileRef.current) fileRef.current.value = "";
  }

  const box = { background: "#fff", border: `1px solid ${C.border}`, borderRadius: 12, padding: 18, boxShadow: C.s1 };

  if (stage === "done" && summary) {
    return (
      <div style={{ ...box, borderColor: `${C.green}55`, background: C.green10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 20 }}>✅</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.green }}>Import complete</div>
            <div style={{ fontSize: 12, color: C.green, marginTop: 2 }}>
              {summary.added || 0} added · {summary.updated || 0} updated
              {summary.skipped ? ` · ${summary.skipped} skipped` : ""}
            </div>
          </div>
          <Btn v="ghost" sx={{ fontSize: 12, padding: "6px 12px" }} onClick={reset}>Import another</Btn>
        </div>
        {summary.errors && summary.errors.length > 0 && (
          <div style={{ marginTop: 12, background: "#fff", border: `1px solid ${C.red}33`, borderRadius: 8, padding: "10px 12px" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.redDark, marginBottom: 4 }}>{summary.errors.length} row(s) skipped:</div>
            {summary.errors.slice(0, 8).map((e, i) => (
              <div key={i} style={{ fontSize: 11.5, color: C.redDark, lineHeight: 1.5 }}>• {e}</div>
            ))}
            {summary.errors.length > 8 && <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2 }}>…and {summary.errors.length - 8} more</div>}
          </div>
        )}
      </div>
    );
  }

  if (stage === "preview" && parsed) {
    const cols = parsed.headers;
    const show = parsed.rows.slice(0, 10);
    return (
      <div style={{ ...box, padding: 0, overflow: "hidden" }}>
        <div style={{ padding: "12px 18px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", background: C.bg2 }}>
          <div>
            <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>Import preview</span>
            <span style={{ fontSize: 11.5, color: C.muted, marginLeft: 10 }}>{parsed.rows.length} row(s)</span>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <Btn v="ghost" sx={{ fontSize: 12, padding: "5px 12px" }} onClick={reset}>Cancel</Btn>
            <Btn sx={{ fontSize: 12, padding: "5px 14px", background: C.green }} onClick={confirmImport}>Confirm import</Btn>
          </div>
        </div>
        {!!err && <div style={{ background: C.red10, color: C.redDark, fontSize: 12, padding: "9px 18px" }}>{err}</div>}
        <div style={{ overflowX: "auto", maxHeight: 260 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ background: C.bg3 }}>
                {cols.map((h) => (
                  <th key={h} style={{ padding: "7px 12px", textAlign: "left", fontSize: 10, fontWeight: 600, color: C.muted, textTransform: "uppercase", borderBottom: `1px solid ${C.border}`, whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {show.map((r, i) => (
                <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : C.bg2 }}>
                  {cols.map((h) => (
                    <td key={h} style={{ padding: "6px 12px", whiteSpace: "nowrap", color: C.text }}>{r[h]}</td>
                  ))}
                </tr>
              ))}
              {parsed.rows.length > 10 && (
                <tr><td colSpan={cols.length} style={{ padding: "8px 12px", textAlign: "center", fontSize: 11, color: C.muted }}>…and {parsed.rows.length - 10} more row(s)</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // idle / importing
  const busy = stage === "importing";
  return (
    <div style={box}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{title}</div>
          {subtitle && <div style={{ fontSize: 12, color: C.muted, marginTop: 3, lineHeight: 1.5 }}>{subtitle}</div>}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Btn v="ghost" sx={{ fontSize: 12, padding: "7px 13px" }} onClick={downloadTemplate}>Download template</Btn>
          <Btn v="ghost" sx={{ fontSize: 12, padding: "7px 13px" }} onClick={doExport}>Export CSV</Btn>
        </div>
      </div>
      {!!err && <div style={{ background: C.red10, border: `1px solid ${C.red}33`, borderRadius: 8, padding: "9px 13px", fontSize: 12, color: C.redDark, marginBottom: 12 }}>{err}</div>}
      <label style={{
        display: "flex", alignItems: "center", gap: 14, padding: "18px 20px",
        border: `1.5px dashed ${busy ? C.blue : C.border}`, borderRadius: 11,
        cursor: busy ? "wait" : "pointer", background: busy ? C.blue5 : C.bg2, transition: "all .15s",
      }}>
        <input ref={fileRef} type="file" accept=".csv,.txt" style={{ display: "none" }} disabled={busy}
          onChange={(e) => handleFile(e.target.files[0])} />
        <div style={{ width: 42, height: 42, borderRadius: 10, background: busy ? C.blue : C.blue10, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={busy ? "#fff" : C.blue} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13.5, fontWeight: 600, color: busy ? C.blue : C.text }}>{busy ? "Importing…" : "Drop a CSV here, or click to choose"}</div>
          <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2 }}>Use the same columns as the template / export. Re-importing an exported file updates existing {noun} (matched by ID) instead of duplicating.</div>
        </div>
      </label>
    </div>
  );
}
